// errorReporter — the errors a learner's browser hits, sent to our own backend
// (ADO #575; POST /api/app-errors, table app_errors, migration 449).
//
// Before the free course launches, Ram watches individual learners to help
// them. A learner who hits a crash or a blank page rarely says so; this is how
// it reaches the admin console's Errors page and that learner's timeline. No
// third-party service.
//
// Three capture points: window 'error', 'unhandledrejection', and the
// top-level React error boundary (components/AppErrorBoundary). Batched like
// learnerEvents; an identical error is sent once a minute at most and a page
// load sends at most MAX_PER_PAGE. The server scrubs and caps again; this side
// only trims. Off under tests. It never throws: every failure is a silent no-op.

import { authFetch } from '@/services/projectService';

const FLUSH_MS = 3000;
const MAX_BATCH = 20;          // the server's cap
const MAX_PER_PAGE = 25;       // one page load never sends more than this
const DEDUPE_MS = 60 * 1000;   // an identical error, once a minute
const MAX_MESSAGE = 500;
const MAX_STACK = 1500;

// Noise every app sees that says nothing about a learner's trouble.
const IGNORED = [
  /^ResizeObserver loop/i,
  /^Script error\.?$/i,          // cross-origin script, no detail to act on
];

let enabled = process.env.NODE_ENV !== 'test';
let installed = false;
let buffer = [];
let timer = null;
let sentThisPage = 0;
const lastSeen = new Map();   // dedupe key -> time last queued

const url = () => `${process.env.REACT_APP_BACKEND_URL || ''}/api/app-errors`;
const release = () => process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA
  || process.env.REACT_APP_GIT_SHA || null;

const clip = (s, n) => (typeof s === 'string' ? s.slice(0, n) : s);

function messageOf(error) {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return `${error.name || 'Error'}: ${error.message || ''}`;
  if (typeof error.message === 'string') return error.message;
  try { return JSON.stringify(error); } catch { return String(error); }
}

// Queue one error. `kind`: 'js.error' | 'promise.rejection' | 'react.render'.
export function reportError(kind, error, detail = {}) {
  try {
    if (!enabled || !kind) return;
    const message = clip(messageOf(error).trim(), MAX_MESSAGE);
    if (!message || IGNORED.some((re) => re.test(message))) return;
    const route = typeof window !== 'undefined' ? window.location.pathname : null;
    const key = `${kind}|${message}|${route}`;
    const now = Date.now();
    const seen = lastSeen.get(key);
    if (seen !== undefined && now - seen < DEDUPE_MS) return;
    if (sentThisPage >= MAX_PER_PAGE) return;
    lastSeen.set(key, now);
    sentThisPage += 1;
    const stack = error && typeof error.stack === 'string' ? clip(error.stack, MAX_STACK) : undefined;
    buffer.push({
      kind,
      message,
      route,
      release: release(),
      detail: { ...(stack ? { stack } : {}), ...detail },
    });
    if (buffer.length >= MAX_BATCH) flushErrors();
    else if (!timer) timer = setTimeout(flushErrors, FLUSH_MS);
  } catch { /* reporting never breaks the app */ }
}

export async function flushErrors({ keepalive = false } = {}) {
  try {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (!buffer.length) return;
    const batch = buffer.slice(0, MAX_BATCH);
    buffer = buffer.slice(MAX_BATCH);
    // One try, no retry: an error report that cannot be sent is not worth a
    // queue that outlives the problem (and offline is itself the likely cause).
    await authFetch(url(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ errors: batch }),
      keepalive,
    });
  } catch { /* swallowed */ }
  try {
    if (buffer.length && !timer) timer = setTimeout(flushErrors, FLUSH_MS);
  } catch { /* swallowed */ }
}

function onError(event) {
  const err = event?.error;
  reportError('js.error', err || event?.message, {
    ...(event?.filename ? { file: clip(String(event.filename), 200) } : {}),
    ...(event?.lineno ? { line: event.lineno } : {}),
    ...(event?.colno ? { col: event.colno } : {}),
  });
}

function onRejection(event) {
  reportError('promise.rejection', event?.reason);
}

function onHidden() {
  if (document.visibilityState === 'hidden') flushErrors({ keepalive: true });
}

// Install the window listeners, once. Safe to call more than once.
export function installErrorReporter() {
  try {
    if (!enabled || installed || typeof window === 'undefined') return;
    installed = true;
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    document.addEventListener('visibilitychange', onHidden);
  } catch { /* swallowed */ }
}

// Test seams only.
export function __setErrorReporterEnabled(on) {
  enabled = !!on;
}

export function __resetErrorReporter() {
  if (typeof window !== 'undefined' && installed) {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    document.removeEventListener('visibilitychange', onHidden);
  }
  installed = false;
  buffer = [];
  if (timer) clearTimeout(timer);
  timer = null;
  sentThisPage = 0;
  lastSeen.clear();
  enabled = process.env.NODE_ENV !== 'test';
}

export function __bufferedErrors() {
  return buffer;
}

export const __limits = { MAX_PER_PAGE, DEDUPE_MS, MAX_BATCH };
